import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { useUserProfile, useUpdateUserProfile, useUpdatePassword } from '../hooks/useUserProfile';
import { UserIcon, EnvelopeIcon, BuildingOfficeIcon, ShieldCheckIcon, CalendarDaysIcon, KeyIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const UserSettings: React.FC = () => {
  const { user } = useAuthStore();
  const { data: profileData, isLoading, error } = useUserProfile();
  const updateProfileMutation = useUpdateUserProfile();
  const updatePasswordMutation = useUpdatePassword();
  
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Update form data when profile data loads
  useEffect(() => {
    if (profileData) {
      setFormData(prev => ({
        ...prev,
        firstName: profileData.firstName || '',
        lastName: profileData.lastName || ''
      }));
    }
  }, [profileData]);

  const handleSave = async () => {
    try {
      // Update profile if name fields changed
      if (formData.firstName !== profileData?.firstName || formData.lastName !== profileData?.lastName) {
        await updateProfileMutation.mutateAsync({
          firstName: formData.firstName,
          lastName: formData.lastName
        });
        toast.success('Profil bilgileri güncellendi');
      }

      // Update password if provided
      if (formData.newPassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          toast.error('Yeni parolalar eşleşmiyor');
          return;
        }

        await updatePasswordMutation.mutateAsync({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword
        });
        toast.success('Parola güncellendi');
      }

      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      setIsEditing(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Güncelleme sırasında hata oluştu');
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: profileData?.firstName || '',
      lastName: profileData?.lastName || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <p className="text-red-700 dark:text-red-300">
            Profil bilgileri yüklenirken hata oluştu: {(error as any).message}
          </p>
        </div>
      </div>
    );
  }

  const currentProfile = profileData || user;

  const formatLastLogin = (lastLogin: string | null) => {
    if (!lastLogin) return 'Henüz giriş yapılmamış';
    return new Date(lastLogin).toLocaleString('tr-TR');
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">Kullanıcı Ayarları</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Profil bilgilerinizi görüntüleyin ve düzenleyin
          </p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sol taraf - Düzenlenebilir bilgiler */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Profil Bilgileri
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ad
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-50 dark:disabled:bg-gray-600"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Soyad
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-50 dark:disabled:bg-gray-600"
                    />
                  </div>
                </div>
              </div>

              {/* Parola değiştirme bölümü */}
              {isEditing && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Parola Değiştir
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Mevcut Parola
                      </label>
                      <input
                        type="password"
                        value={formData.currentPassword}
                        onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Yeni Parola
                      </label>
                      <input
                        type="password"
                        value={formData.newPassword}
                        onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Yeni Parola (Tekrar)
                      </label>
                      <input
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3">
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                  >
                    Düzenle
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleSave}
                      disabled={updateProfileMutation.isPending || updatePasswordMutation.isPending}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {updateProfileMutation.isPending || updatePasswordMutation.isPending ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={updateProfileMutation.isPending || updatePasswordMutation.isPending}
                      className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      İptal
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Sağ taraf - Sadece okunabilir bilgiler */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Hesap Bilgileri
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">E-posta</p>
                      <p className="text-sm text-gray-900 dark:text-white">{currentProfile?.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Tenant</p>
                      <p className="text-sm text-gray-900 dark:text-white">{currentProfile?.tenantId}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <ShieldCheckIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Rol</p>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {currentProfile?.role === 'tenant_admin' ? 'Tenant Yöneticisi' : 
                         currentProfile?.role === 'super_admin' ? 'Super Admin' : 
                         currentProfile?.role === 'admin' ? 'Yönetici' : 
                         currentProfile?.role || 'Kullanıcı'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <CalendarDaysIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Son Giriş</p>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {formatLastLogin(currentProfile?.lastLogin || null)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <UserIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Hesap Durumu</p>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {currentProfile?.status === 'active' ? 'Aktif' : currentProfile?.status || 'Bilinmiyor'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <KeyIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">2FA</p>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {(currentProfile as any)?.mfaEnabled ? 'Etkin' : 'Devre Dışı'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSettings;